import contactPointSchema from '../../common/structuredData/types/contactPoint';
import countrySchema from '../../common/structuredData/types/country';
import imageObject from '../../common/structuredData/types/imageObject';
import postalAddress from '../../common/structuredData/types/postalAddress';

/**
 * Organization type
 * DOC: https://schema.org/Organization
 */
export const organizationType = ({
  url,
  name,
  sameAs,
  logo,
  alternateName,
  legalName,
  slogan,
  description,
  keywords,
  foundingDate,
  merchantReturnPolicy,
  foundingLocation,
  vatID,
  taxID,
  founder,
  address,
  addressCountry,
  publishingPrinciples,
  knowsLanguage,
  knowsAbout,
  parentOrganization,
  subOrganization,
  contactPoint,
  subjectOf
}) => {
  if (!url || typeof url === 'undefined') {
    throw new Error('url argument is required');
  }
  if (!name || typeof name === 'undefined') {
    throw new Error('name argument is required');
  }
  const logoSchema = ({
    id,
    url,
    caption
  }) => `"logo": ${JSON.stringify(imageObject({
    id,
    url,
    caption
  }))}`;
  const result = [];
  result.push('"@context": "http://schema.org"');
  result.push('"@type": "Organization"');
  result.push(`"@id": "${url}#organization"`);
  result.push(`"name": "${name}"`);
  result.push(`"url": "${url}"`);
  if (alternateName && alternateName.length > 0) {
    result.push(`"alternateName": [
      ${alternateName.map(name => `"${name}"`).join(',')}
    ]`);
  }
  if (legalName) {
    result.push(`"legalName":"${legalName}"`);
  }
  if (sameAs && sameAs.length > 0) {
    result.push(`"sameAs": [
      ${sameAs.map(link => `"${link}"`).join(',')}
    ]`);
  }
  if (address) {
    result.push(`"address": ${JSON.stringify(postalAddress(address))}`);
  }
  if (slogan) {
    result.push(`"slogan":"${slogan}"`);
  }
  if (description) {
    result.push(`"description":"${description}"`);
  }
  if (keywords && keywords.length > 0) {
    result.push(`"keywords": [
      ${keywords.map(keyword => `"${keyword}"`).join(',')}
    ]`);
  }
  if (knowsLanguage && knowsLanguage.length > 0) {
    result.push(`"knowsLanguage": [
      ${knowsLanguage.map(keyword => `"${keyword}"`).join(',')}
    ]`);
  }
  if (foundingDate) {
    result.push(`"foundingDate": "${foundingDate}"`);
  }
  if (foundingLocation) {
    result.push(`"foundingLocation": {
      "@type": "Place",
      "address": ${JSON.stringify(postalAddress({
      addressLocality: foundingLocation.address.addressLocality,
      addressCountry: foundingLocation.address.addressCountry,
      addressRegion: foundingLocation.address.addressRegion
    }))}
    }`);
  }
  if (vatID) {
    result.push(`"vatID": "${vatID}"`);
  }
  if (taxID) {
    result.push(`"taxID":"${taxID}"`);
  }
  if (merchantReturnPolicy) {
    result.push(`"hasMerchantReturnPolicy": {
      "@type": "MerchantReturnPolicy",
      "merchantReturnLink": "${merchantReturnPolicy.merchantReturnLink}"
    }`);
  }
  if (logo) {
    result.push(`${logoSchema({
      id: logo.id,
      url: logo.url,
      caption: logo.caption
    })}`);
  }
  if (founder) {
    result.push(`"founder": [
      ${founder.map(person => `{
        "@type": "Person",
        "givenName" : "${person.givenName}",
        "familyName": "${person.familyName}"
      }`)}
    ]`);
  }
  if (publishingPrinciples) {
    result.push(`"publishingPrinciples":"${publishingPrinciples}"`);
  }
  if (knowsAbout && knowsAbout.length > 0) {
    result.push(`"knowsAbout": [
      ${knowsAbout.map(url => `"${url}"`).join(',')}
    ]`);
  }
  if (contactPoint) {
    result.push(`"contactPoint": ${JSON.stringify(contactPointSchema({
      contactType: contactPoint.contactType,
      email: contactPoint.email,
      url: contactPoint.url
    }))}`);
  }
  if (subjectOf && subjectOf.length > 0) {
    result.push(`"subjectOf": [
      ${subjectOf.map(el => JSON.stringify({
      '@type': 'Thing',
      name: `"${el}"`
    }))}]`);
  }
  if (addressCountry) {
    result.push(`"addressCountry": ${JSON.stringify(countrySchema({
      name: addressCountry.address.name
    }))}`);
  }
  if (subOrganization) {
    result.push(`"subOrganization": [
      ${subOrganization.map(organization => organization)}
    ]`);
  }
  if (parentOrganization) {
    result.push(`"parentOrganization": ${parentOrganization}`);
  }
  return `{${result.filter(item => item).join(',')}}`;
};